import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import HomeRepairServiceOutlinedIcon from "@mui/icons-material/HomeRepairServiceOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";

import { EvotruxDivider } from "components/EvotruxDivider";
import {
  EQUIPMENT_VALUE_TO_LABEL_MAP,
  MAX_VISIBLE_EQUIPMENT_TYPES,
  SERVICE_AREAS_TO_LABEL_MAP,
} from "constants/index";
import { useDisclosure } from "hooks";

import { CarrierInformationEquipmentsModal } from "./CarrierInformationEquipmentsModal";

import type { CompanyProfileAbout } from "types/company";

interface CarrierInformationProps {
  businessType: BusinessTypeValue | null;
  profileAbout: CompanyProfileAbout | null;
}

interface CarrierTypeProps {
  businessType: string | null;
  numberOfTrucks: number | null;
  numberOfTrailers: number | null;
}

function CarrierType(props: CarrierTypeProps) {
  const { businessType, numberOfTrucks, numberOfTrailers } = props;
  if (businessType === "TRUCKING_COMPANY") {
    return (
      <Stack spacing={1}>
        <Typography variant="h6">Trucking Company</Typography>
        <Typography variant="body1">
          We own and operate our own fleet of trucks and drivers. We primarily
          handle freight transportation using our company assets.
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Typography>Number of Trucks:</Typography>
          {numberOfTrucks !== null ? (
            <Typography>{numberOfTrucks.toLocaleString()}</Typography>
          ) : (
            <Typography color="text.secondary">Not added yet</Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <Typography>Number of Trailers:</Typography>
          {numberOfTrailers !== null ? (
            <Typography>{numberOfTrailers.toLocaleString()}</Typography>
          ) : (
            <Typography color="text.secondary">Not added yet</Typography>
          )}
        </Stack>
      </Stack>
    );
  }
  if (businessType === "BROKER") {
    return (
      <Stack spacing={1}>
        <Typography variant="h6">Non-Asset Broker</Typography>
        <Typography variant="body1">
          We coordinate freight transportation on behalf of shippers by
          partnering with outside carriers. We do not own or operate our own
          trucks.
        </Typography>
      </Stack>
    );
  }
  return null;
}

export function CarrierInformation(props: CarrierInformationProps) {
  const { businessType, profileAbout } = props;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const equipmentTypes = profileAbout?.equipment_types ?? [];
  const remainingCount = equipmentTypes.length - MAX_VISIBLE_EQUIPMENT_TYPES;

  return (
    <Stack>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Carrier Information
      </Typography>
      <EvotruxDivider />
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
          <Card
            sx={{
              width: "100%",
              maxHeight: 300,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent
              sx={{
                flex: 1,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LocalShippingOutlinedIcon
                    color="primary"
                    fontSize="medium"
                  />
                  <Typography variant="h5">Carrier Type</Typography>
                </Stack>
                {businessType ? (
                  <CarrierType
                    businessType={businessType}
                    numberOfTrucks={profileAbout?.number_of_trucks ?? null}
                    numberOfTrailers={profileAbout?.number_of_trailers ?? null}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    This carrier has not specified business type
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
          <Card
            sx={{
              width: "100%",
              maxHeight: 300,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent
              sx={{
                flex: 1,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <MapOutlinedIcon color="primary" fontSize="medium" />
                  <Typography variant="h5">Service Areas</Typography>
                </Stack>
                {profileAbout &&
                (profileAbout?.service_areas?.length ?? 0) > 0 ? (
                  <Stack spacing={1}>
                    {profileAbout.service_areas.map((area) => (
                      <Stack
                        direction="row"
                        alignItems="center"
                        key={area}
                        spacing={1}
                      >
                        <DoneAllOutlinedIcon color="success" />
                        <Typography>
                          {SERVICE_AREAS_TO_LABEL_MAP[area]}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    This carrier has not specified service areas
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
          <Card
            sx={{
              width: "100%",
              maxHeight: 300,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardHeader
              title={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <HomeRepairServiceOutlinedIcon
                    color="primary"
                    fontSize="medium"
                  />
                  <Typography variant="h5">Equipment Type</Typography>
                </Stack>
              }
              action={
                remainingCount > 0 && (
                  <Button onClick={onOpen}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography color="primary">View All</Typography>
                      <ArrowForwardIosIcon color="primary" fontSize="small" />
                    </Stack>
                  </Button>
                )
              }
              sx={{ paddingBottom: 0 }}
            />
            <CardContent
              sx={{
                flex: 1,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Stack spacing={1}>
                {equipmentTypes.length > 0 ? (
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {equipmentTypes
                      .slice(0, MAX_VISIBLE_EQUIPMENT_TYPES)
                      .map((type) => (
                        <Chip
                          label={EQUIPMENT_VALUE_TO_LABEL_MAP[type]}
                          color="primary"
                          variant="outlined"
                          key={type}
                        />
                      ))}
                    {remainingCount > 0 && (
                      <Chip label={`+${remainingCount} more`} color="default" />
                    )}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    This carrier has not specified equipment types
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <CarrierInformationEquipmentsModal
        modalOpen={isOpen}
        onModalClose={onClose}
        equipmentTypes={equipmentTypes}
      />
    </Stack>
  );
}
